import React, { useState } from 'react';
import { AlertTriangle, Clock, User, X } from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';

/**
 * ConflictModal - Warns about schedule overlaps when drag-dropping jobs
 * 
 * Design philosophy:
 * - Warn but don't block (teams exist!)
 * - Show what it conflicts with
 * - Offer smart alternatives
 * - Allow users to disable warnings
 */
const ConflictModal = ({
  isOpen,
  onClose,
  onConfirm,
  onSelectAlternative,
  draggedJob,
  conflictingJobs,
  targetTime,
  targetDate,
  nextAvailableTime,
  isOwner = true,
}) => {
  const [dontWarnAgain, setDontWarnAgain] = useState(false);

  if (!isOpen) return null;

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleConfirm = (choice) => {
    onConfirm(choice, dontWarnAgain);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-amber-50 border-b border-amber-100 px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle size={20} className="text-amber-600" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Schedule Overlap</h2>
                <p className="text-sm text-amber-700">
                  {formatDate(targetDate)} at {formatTime(targetTime)}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* What we're moving */}
          <div className="mb-4">
            <p className="text-sm text-gray-500 mb-2">Moving:</p>
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <div className="font-medium text-gray-900">
                {draggedJob?.customer?.name}
              </div>
              <div className="text-sm text-gray-600">
                {draggedJob?.address}
              </div>
              {isOwner && draggedJob?.quoteAmount > 0 && (
                <div className="text-sm font-medium text-blue-600 mt-1">
                  {formatCurrency(draggedJob.quoteAmount)}
                </div>
              )}
            </div>
          </div>

          {/* Conflicts with */}
          <div className="mb-4">
            <p className="text-sm text-gray-500 mb-2">Overlaps with:</p>
            <div className="space-y-2">
              {conflictingJobs.map((job) => (
                <div
                  key={job.id}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-3"
                >
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                    <Clock size={14} />
                    <span>
                      {formatTime(job.scheduledTime || job.time)}
                      {job.estimatedDurationMinutes && (
                        <span className="text-gray-400">
                          {' '}({job.estimatedDurationMinutes} min)
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User size={14} className="text-gray-400" />
                    <span className="font-medium text-gray-900">
                      {job.customer?.name}
                    </span>
                  </div>
                  {isOwner && job.quoteAmount > 0 && (
                    <div className="text-sm text-gray-600 mt-1">
                      {formatCurrency(job.quoteAmount)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Options */}
          <div className="space-y-2">
            <button
              onClick={() => handleConfirm('schedule-anyway')}
              className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <div className="font-medium text-gray-900">
                Schedule anyway
              </div>
              <div className="text-sm text-gray-500">
                I have a team or can handle the overlap
              </div>
            </button>

            {nextAvailableTime && (
              <button
                onClick={() => onSelectAlternative(nextAvailableTime)}
                className="w-full text-left px-4 py-3 rounded-lg border border-primary-200 bg-primary-50 hover:bg-primary-100 transition-colors"
              >
                <div className="font-medium text-primary-700">
                  Move to {formatTime(nextAvailableTime)}
                </div>
                <div className="text-sm text-primary-600">
                  Next available time slot
                </div>
              </button>
            )}

            <button
              onClick={onClose}
              className="w-full text-center px-4 py-3 text-gray-600 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
          </div>

          {/* Don't warn again */}
          <label className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100 cursor-pointer">
            <input
              type="checkbox"
              checked={dontWarnAgain}
              onChange={(e) => setDontWarnAgain(e.target.checked)}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-500">
              Don't warn me about overlaps
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default ConflictModal;